import type {
  FeedRestoreRecord,
  FeedRestoreStorage as CoreFeedRestoreStorage
} from '../core/session';
import { isRecord } from '../core/record';
import type { ChannelId } from '../core/types';

const KEY_PREFIX = 'feed:restore:v1';

/** localStorage-backed feed restore records; every operation is best-effort. */
export class FeedRestoreStorage implements CoreFeedRestoreStorage {
  read(accountId: string, conversationId: ChannelId): FeedRestoreRecord | undefined {
    try {
      const raw = globalThis.localStorage?.getItem(storageKey(accountId, conversationId));
      if (!raw) return undefined;
      const parsed: unknown = JSON.parse(raw);
      // SAFETY: Storage validation happens in the core session before any restore record is trusted.
      return isRecord(parsed) ? parsed as unknown as FeedRestoreRecord : undefined;
    } catch {
      return undefined;
    }
  }

  write(record: FeedRestoreRecord): void {
    try {
      globalThis.localStorage?.setItem(
        storageKey(record.accountId, record.conversationId),
        JSON.stringify(record)
      );
    } catch {
      // Restore state is opportunistic; private-mode/quota failures should not affect the feed.
    }
  }

  remove(accountId: string, conversationId: ChannelId): void {
    try {
      globalThis.localStorage?.removeItem(storageKey(accountId, conversationId));
    } catch {
      // Restore state is opportunistic; private-mode/quota failures should not affect the feed.
    }
  }
}

/** Builds the localStorage key for one account/conversation restore record. */
export function storageKey(accountId: string, conversationId: ChannelId): string {
  return `${KEY_PREFIX}:${accountId}:${conversationId}`;
}
